/**
 * OAuth callback — backend redirects here after exchanging the code with the
 * provider. Token + email arrive in the URL fragment (#) so they don't leak
 * into server logs / Referer headers:
 *
 *   /auth/oauth/callback#token=<jwt>&email=<email>&provider=google
 *
 * On error backend redirects with `#error=<code>` and we render a friendly page.
 */
import { useEffect, useState } from 'react'
import { ErrorLayout, ErrorBlock } from '@/components/layout/ErrorLayout'
import { Button } from '@/components/ui/Button'
import { authApi } from '@/api/auth'
import { useAuth } from '@/stores/auth'
import { redirectAfterAuth } from '@/lib/authRedirect'

type State =
  | { kind: 'pending' }
  | { kind: 'error'; title: string; body: string }

function parseFragment(): URLSearchParams {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  return new URLSearchParams(hash)
}

export function OAuthCallback() {
  const setSession = useAuth((s) => s.setSession)
  const [state, setState] = useState<State>({ kind: 'pending' })

  useEffect(() => {
    const frag = parseFragment()
    const token = frag.get('token')
    const email = frag.get('email') ?? ''
    const provider = (frag.get('provider') ?? 'google') as 'google' | 'yandex'
    const errorCode = frag.get('error')

    if (errorCode) {
      const map: Record<string, { title: string; body: string }> = {
        bad_request: {
          title: 'Сессия устарела ⏰',
          body: 'Запрос на вход устарел или повреждён. Попробуй войти ещё раз.',
        },
        oauth_failed: {
          title: 'Не удалось подтвердить вход',
          body: `Провайдер (${provider === 'yandex' ? 'Яндекс ID' : 'Google'}) отклонил авторизацию. Попробуй ещё раз или войди по email.`,
        },
        oauth_disabled: {
          title: 'Вход через провайдера отключён',
          body: 'OAuth временно недоступен. Войди по email и паролю.',
        },
        server_error: {
          title: 'Сервер не отвечает',
          body: 'Что-то пошло не так у нас. Попробуй позже.',
        },
      }
      const e = map[errorCode] ?? {
        title: 'Не удалось войти 😕',
        body: 'Что-то пошло не так. Попробуй позже или войди по email.',
      }
      setState({ kind: 'error', ...e })
      return
    }

    if (!token) {
      setState({
        kind: 'error',
        title: 'Не удалось войти 😕',
        body: 'Провайдер не вернул токен. Попробуй ещё раз или используй email.',
      })
      return
    }

    // Validate by calling /me — guards against tampered tokens and gives us a
    // fresh user profile (avatar/display_name) instead of a stub.
    localStorage.setItem('auth_token', token)
    authApi
      .me()
      .then((user) => {
        setSession({
          access_token: token,
          token_type: 'Bearer',
          expires_in: 30 * 24 * 3600,
          user,
        })
        // Remember email for future PIN-login screen
        if (email) localStorage.setItem('agentceo_last_login_email', email)
        // Clean fragment from the URL bar before navigating away
        window.history.replaceState(null, '', window.location.pathname)
        redirectAfterAuth(user)
      })
      .catch(() => {
        localStorage.removeItem('auth_token')
        setState({
          kind: 'error',
          title: 'Не удалось войти 😕',
          body: 'Токен не прошёл проверку. Попробуй ещё раз или войди по email.',
        })
      })
  }, [setSession])

  if (state.kind === 'pending') {
    return (
      <ErrorLayout hideBack>
        <ErrorBlock
          icon="loader-2"
          iconTone="brand"
          title="Входим..."
          body={<p className="text-sm text-text-muted">Подтверждаем авторизацию.</p>}
        />
      </ErrorLayout>
    )
  }

  return (
    <ErrorLayout>
      <ErrorBlock
        icon="alert-triangle"
        iconTone="warning"
        title={state.title}
        body={<p>{state.body}</p>}
        actions={
          <>
            <Button asChild variant="primary"><a href="/auth/login">Попробовать снова</a></Button>
            <Button asChild variant="ghost"><a href="/auth/register">Создать аккаунт</a></Button>
          </>
        }
      />
    </ErrorLayout>
  )
}
